import {
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormGroup,
  TextField,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import Grid2 from "@mui/material/Grid2";
import { Slider } from "@mui/material";
import { saveFormDataToLocal } from "../Utility/FormDataTempStorage";
import NotesTextField from "../Worklist/NotesTextField";
import GenericTextField from "../common/GenericTextField";
import {
  painLevelValue,
} from "../Utility/Constants";

function PhysiotherapyPrescriptionDisplay({
  formData,
  readOnly,
  setFormData,
  uniqueId,
}) {
  // ── helpers ──────────────────────────────────────────────────────────────

  const handleFormDataChange = (updatedValues) => {
    saveFormDataToLocal(uniqueId, updatedValues);
  };

  const renderReadOnlyValue = (label, value) => (
    <Grid2 container spacing={1} alignItems="center">
      {label !== "" && (
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <Typography fontWeight="bold">{label}</Typography>
        </Grid2>
      )}
      <Grid2 size={{ xs: 12, sm: label !== "" ? 8 : 12 }}>
        {value === "" || value == null ? (
          <Typography fontStyle="italic" color="text.secondary">
            Not mentioned
          </Typography>
        ) : (
          <Typography>{value}</Typography>
        )}
      </Grid2>
    </Grid2>
  );

  // helper: comma-joined selected items from checkbox arrays
  const getSelectedItems = (items) => {
    if (!items) return "";
    return items
      .filter((item) => item.selected)
      .map((item) => item.displayValue)
      .join(", ");
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Grid2 container size={12} spacing={2}>
      {/* ── Complaint and Medical History ─────────────────────────────── */}
      <Grid2 size={12}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            <Typography component="span">
              Complaint and Medical History
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "16px" }}>
            <Grid2 container size={12} spacing={3}>
              {/* Chief Complaint */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue("Chief Complaint", formData?.complaint)
                ) : (
                  <NotesTextField
                    value={formData?.complaint || ""}
                    onChange={(newValue) => {
                      const updated = { ...formData, complaint: newValue };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Chief Complaint"
                    rows={4}
                    tagsForAutocomplete={["physiotherapy-complaint"]}
                  />
                )}
              </Grid2>

              {/* Medical History checkboxes */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Medical History",
                    getSelectedItems(formData?.medicalHistoryConditions),
                  )
                ) : (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">
                      Medical History (Select applicable conditions)
                    </FormLabel>
                    <FormGroup row>
                      {formData?.medicalHistoryConditions?.map(
                        (condition, index) => (
                          <FormControlLabel
                            key={index}
                            control={
                              <Checkbox
                                checked={condition.selected}
                                onChange={(e) => {
                                  const newConditions = [
                                    ...formData.medicalHistoryConditions,
                                  ];
                                  newConditions[index].selected =
                                    e.target.checked;
                                  const updated = {
                                    ...formData,
                                    medicalHistoryConditions: newConditions,
                                  };
                                  setFormData(updated);
                                  handleFormDataChange(updated);
                                }}
                              />
                            }
                            label={condition.displayValue}
                          />
                        ),
                      )}
                    </FormGroup>
                  </FormControl>
                )}
              </Grid2>

              {/* Medical History Notes */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Medical History Notes",
                    formData?.medicalHistoryNotes,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.medicalHistoryNotes || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        medicalHistoryNotes: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Medical History Notes"
                    rows={4}
                  />
                )}
              </Grid2>

              {/* Surgery Details */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Surgery Details",
                    formData?.surgeryDetails,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.surgeryDetails || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        surgeryDetails: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Surgery Details"
                    rows={4}
                  />
                )}
              </Grid2>
            </Grid2>
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* ── Assessment ────────────────────────────────────────────────── */}
      <Grid2 size={12}>
        <Accordion variant="outlined">
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            <Typography component="span">Assessment</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "16px" }}>
            <Grid2 container size={12} spacing={3}>
              {/* Pain Level */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Pain Level (VAS)",
                    formData?.painLevel != null
                      ? `${formData.painLevel} / 10`
                      : null,
                  )
                ) : (
                  <>
                    <Typography>Pain Level (VAS)</Typography>
                    <Grid2 container size={10} sx={{ mt: 1 }}>
                      <Slider
                        value={Number(formData?.painLevel) || 0}
                        marks={painLevelValue}
                        step={1}
                        min={0}
                        max={10}
                        valueLabelDisplay="auto"
                        onChange={(e) => {
                          const updated = {
                            ...formData,
                            painLevel: Number(e.target.value),
                          };
                          setFormData(updated);
                          handleFormDataChange(updated);
                        }}
                      />
                    </Grid2>
                  </>
                )}
              </Grid2>

              {/* Pain Level Notes */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Pain Level Notes",
                    formData?.painLevelNotes,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.painLevelNotes || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        painLevelNotes: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Pain Level Notes"
                    rows={4}
                  />
                )}
              </Grid2>

              {/* Pain Types */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Pain Types",
                    getSelectedItems(formData?.painTypes),
                  )
                ) : (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">
                      Select applicable pain types
                    </FormLabel>
                    <FormGroup row>
                      {formData?.painTypes?.map((painType, index) => (
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox
                              checked={painType.selected}
                              onChange={(e) => {
                                const newConditions = [...formData.painTypes];
                                newConditions[index].selected = e.target.checked;
                                const updated = {
                                  ...formData,
                                  painTypes: newConditions,
                                };
                                setFormData(updated);
                                handleFormDataChange(updated);
                              }}
                            />
                          }
                          label={painType.displayValue}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                )}
              </Grid2>

              {/* Pain Type Notes */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Pain Type Notes",
                    formData?.painTypeNotes,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.painTypeNotes || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        painTypeNotes: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Pain Type Notes"
                    rows={4}
                  />
                )}
              </Grid2>

              {/* Range of Motion */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Range of Motion",
                    formData?.rangeOfMotion,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.rangeOfMotion || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        rangeOfMotion: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Range of Motion"
                    rows={3}
                  />
                )}
              </Grid2>

              {/* Muscle Strength */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Muscle Strength",
                    formData?.muscleStrength,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.muscleStrength || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        muscleStrength: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Muscle Strength"
                    rows={3}
                  />
                )}
              </Grid2>

              {/* Muscle Tightness */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Muscle spasm \\ Tightness \\ Trigger points",
                    formData?.muscleTightness,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.muscleTightness || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        muscleTightness: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Muscle spasm \ Tightness \ Trigger points"
                    rows={3}
                  />
                )}
              </Grid2>

              {/* Special Tests */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue("Special Test", formData?.specialTests)
                ) : (
                  <NotesTextField
                    value={formData?.specialTests || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        specialTests: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Special Test"
                    rows={3}
                  />
                )}
              </Grid2>
            </Grid2>
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* ── Prescribed Treatment ──────────────────────────────────────── */}
      <Grid2 size={12}>
        <Accordion variant="outlined">
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            <Typography component="span">Prescribed Treatment</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "16px" }}>
            <Grid2 container size={12} spacing={3}>
              {/* Treatment Plan */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue("Treatment Plan", formData?.treatmentPlan)
                ) : (
                  <NotesTextField
                    value={formData?.treatmentPlan || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        treatmentPlan: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Treatment Plan"
                    rows={5}
                  />
                )}
              </Grid2>

              {/* Suggested Sessions */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Suggested no. of sessions",
                    formData?.suggestedSessions,
                  )
                ) : (
                  <GenericTextField
                    label="Suggested no. of sessions"
                    type="number"
                    value={formData?.suggestedSessions || ""}
                    onChange={(e) => {
                      const updated = {
                        ...formData,
                        suggestedSessions: e.target.value,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    placeholder="Enter number of sessions..."
                  />
                )}
              </Grid2>

              {/* Short Term Goals */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Short term treatment goals",
                    formData?.shortTermTreatmentGoals,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.shortTermTreatmentGoals || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        shortTermTreatmentGoals: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Short term treatment goals"
                    rows={4}
                  />
                )}
              </Grid2>

              {/* Treatment Methods */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Treatment Methods",
                    formData?.treatmentMethods
                      ?.filter((m) => m.selected)
                      .map((m) =>
                        m.value === "Others" && m.additionalText
                          ? `${m.displayValue} (${m.additionalText})`
                          : m.displayValue,
                      )
                      .join(", "),
                  )
                ) : (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">Treatment Methods</FormLabel>
                    <FormGroup row>
                      {formData?.treatmentMethods?.map((treatment, index) => (
                        <FormControlLabel
                          key={index}
                          control={
                            <Checkbox
                              checked={treatment.selected}
                              onChange={(e) => {
                                const newConditions = [
                                  ...formData.treatmentMethods,
                                ];
                                newConditions[index].selected = e.target.checked;
                                const updated = {
                                  ...formData,
                                  treatmentMethods: newConditions,
                                };
                                setFormData(updated);
                                handleFormDataChange(updated);
                              }}
                            />
                          }
                          label={treatment.displayValue}
                        />
                      ))}
                    </FormGroup>
                    {/* "Others" extra text field */}
                    {formData?.treatmentMethods?.find(
                      (t) => t.value === "Others" && t.selected,
                    ) && (
                      <TextField
                        label="Specify Other"
                        size="small"
                        sx={{ mt: 1 }}
                        value={
                          formData.treatmentMethods.find(
                            (t) => t.value === "Others",
                          )?.additionalText || ""
                        }
                        onChange={(e) => {
                          const newConditions = [...formData.treatmentMethods];
                          const idx = newConditions.findIndex(
                            (t) => t.value === "Others",
                          );
                          newConditions[idx].additionalText = e.target.value;
                          const updated = {
                            ...formData,
                            treatmentMethods: newConditions,
                          };
                          setFormData(updated);
                          handleFormDataChange(updated);
                        }}
                      />
                    )}
                  </FormControl>
                )}
              </Grid2>

              {/* Long Term Goals */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue(
                    "Long term treatment goals",
                    formData?.longTermTreatmentGoals,
                  )
                ) : (
                  <NotesTextField
                    value={formData?.longTermTreatmentGoals || ""}
                    onChange={(newValue) => {
                      const updated = {
                        ...formData,
                        longTermTreatmentGoals: newValue,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Long term treatment goals"
                    rows={4}
                  />
                )}
              </Grid2>

              {/* Do's and Don'ts */}
              <Grid2 size={12}>
                {readOnly ? (
                  renderReadOnlyValue("Do's and Dont's", formData?.dosDonts)
                ) : (
                  <NotesTextField
                    value={formData?.dosDonts || ""}
                    onChange={(newValue) => {
                      const updated = { ...formData, dosDonts: newValue };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    label="Do's and Dont's"
                    rows={4}
                    tagsForAutocomplete={[
                      "physiotherapy-dos",
                      "physiotherapy-donts",
                    ]}
                  />
                )}
              </Grid2>
            </Grid2>
          </AccordionDetails>
        </Accordion>
      </Grid2>
    </Grid2>
  );
}

export default PhysiotherapyPrescriptionDisplay;